import { focusCvField, stepForCvField } from "@/lib/cvBuilderPresentation";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { CvBuilderProvider, useCvBuilder } from "@/stores/cvBuilderStore";
import StepWizard from "@/components/admin/recruitment/cv-builder/StepWizard";
import CandidateStep from "@/components/admin/recruitment/cv-builder/CandidateStep";
import SkillsStep from "@/components/admin/recruitment/cv-builder/SkillsStep";
import ExperiencesStep from "@/components/admin/recruitment/cv-builder/ExperiencesStep";
import MissionsStep from "@/components/admin/recruitment/cv-builder/MissionsStep";
import EducationStep from "@/components/admin/recruitment/cv-builder/EducationStep";
import PreviewStep from "@/components/admin/recruitment/cv-builder/PreviewStep";
import CvImportSelection from "@/components/admin/recruitment/cv-builder/CvImportSelection";
import CvImportStep from "@/components/admin/recruitment/cv-builder/CvImportStep";
import ConfidenceScoreView from "@/components/admin/recruitment/cv-builder/ConfidenceScoreView";
import { Loader2, CheckCircle2, History, AlertTriangle, AlertCircle, Clock3 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";

const STEPS = [
    { id: "candidate", title: "Candidat", description: "Informations personnelles et accroche" },
    { id: "skills", title: "Skills", description: "Compétences phares et catégories" },
    { id: "experiences", title: "Expériences", description: "Parcours en entreprise" },
    { id: "missions", title: "Missions", description: "Détails techniques par expérience" },
    { id: "education", title: "Formation", description: "Diplômes, langues et certifs" },
    { id: "preview", title: "Preview", description: "Vérification finale et JSON" },
];

const downloadPdfBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
    }, 0);
};

const CvBuilderContent = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const { draftId: routeDraftId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsString = searchParams.toString();
    const isNewDraftRequested = searchParams.get("new") === "1" || searchParams.get("new") === "true";
    const navigate = useNavigate();
    const {
        loadDraft,
        saveDraft,
        generateDraft,
        resetDraft,
        loadLocalDraft,
        isSaving,
        isLoading,
        completionScore,
        draft,
        draftId: activeDraftId,
        addExperience,
        importMode,
        isUploading,
        importProgress,
        importError,
        importWarnings,
        manualReviewRequired,
        validationStatus,
        verificationProgress,
        lastImportResult,
        setImportMode,
        parseScore,
        parseWarnings,
        mandatoryReview,
        parseUploadedCv,
        resetImportState,
        duplicatePrompt,
        setDuplicatePrompt,
        anomalies,
        scoringDimensions,
        resolveAnomaly,
    } = useCvBuilder();

    const [correctionTarget, setCorrectionTarget] = useState(null);
    const [hasLocalDraft, setHasLocalDraft] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const pendingRouteSyncDraftIdRef = useRef(null);

    useEffect(() => {
        if (routeDraftId) {
            const isPendingRouteSync = pendingRouteSyncDraftIdRef.current === routeDraftId;
            if (routeDraftId === activeDraftId) {
                if (isPendingRouteSync) {
                    pendingRouteSyncDraftIdRef.current = null;
                }
                return;
            }
            if (!isPendingRouteSync) {
                loadDraft(routeDraftId);
            }
        } else if (isNewDraftRequested && importMode === "none") {
            // Only trigger reset and selection mode if we are not already in an import flow
            resetDraft();
            setImportMode(new URLSearchParams(searchParamsString).get("import") === "1" ? "upload" : new URLSearchParams(searchParamsString).get("manual") === "1" ? "none" : "selection");
            // Clean up the URL
            const newParams = new URLSearchParams(searchParamsString);
            newParams.delete("new");
            newParams.delete("import");
            newParams.delete("manual");
            setSearchParams(newParams, { replace: true });
        } else if (!routeDraftId && !isNewDraftRequested) {
            // Check if a local draft exists to show the modal
            const saved = sessionStorage.getItem("cv_builder_draft");
            if (saved && !activeDraftId) {
                setHasLocalDraft(true);
            }
        }
    }, [
        routeDraftId,
        activeDraftId,
        isNewDraftRequested,
        importMode,
        searchParamsString,
        loadDraft,
        resetDraft,
        setImportMode,
        setSearchParams,
    ]);

    const handleResumeLocal = () => {
        loadLocalDraft();
        setHasLocalDraft(false);
    };

    const handleIgnoreLocal = () => {
        sessionStorage.removeItem("cv_builder_draft");
        resetDraft();
        setHasLocalDraft(false);
    };

    const [lastSavedDraft, setLastSavedDraft] = useState(null);
    const [showSaved, setShowSaved] = useState(false);
    const [saveError, setSaveError] = useState("");
    const serializedDraft = JSON.stringify(draft);
    const isDraftEmpty = !draft.candidate?.full_name && !draft.candidate?.title && (draft.experiences?.length || 0) === 0;
    const hasUnsavedChanges = !isDraftEmpty && serializedDraft !== lastSavedDraft;

    useEffect(() => {
        // Autosave logic: trigger if draft changed and isn't empty
        if (draft && serializedDraft !== lastSavedDraft && !isDraftEmpty && !isSaving && !isLoading) {
            const timer = setTimeout(() => {
                const snapshot = serializedDraft;
                setSaveError("");
                saveDraft().then((newId) => {
                    if (!newId) {
                        setSaveError("La sauvegarde automatique a échoué. Réessayez avec le bouton Enregistrer.");
                        return;
                    }
                    setLastSavedDraft(snapshot);
                    setShowSaved(true);
                    setTimeout(() => setShowSaved(false), 3000);

                    // If we just created a new draft, update the URL
                    if (!routeDraftId) {
                        pendingRouteSyncDraftIdRef.current = newId;
                        navigate(`/admin/recrutement/cv-builder/${newId}`, { replace: true });
                    }
                }).catch(() => setSaveError("La sauvegarde automatique a échoué. Réessayez avec le bouton Enregistrer."));
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [
        draft,
        serializedDraft,
        isDraftEmpty,
        routeDraftId,
        saveDraft,
        lastSavedDraft,
        navigate,
        isSaving,
        isLoading,
    ]);

    // Initialize lastSavedDraft once draft is loaded
    useEffect(() => {
        if (draft && !lastSavedDraft && routeDraftId && !isLoading && activeDraftId === routeDraftId) {
            setLastSavedDraft(JSON.stringify(draft));
        }
    }, [draft, routeDraftId, lastSavedDraft, isLoading, activeDraftId]);

    useEffect(() => {
        if (!hasUnsavedChanges) return undefined;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Register event listeners for deep navigation from Missions Step
    useEffect(() => {
        const handlePrevStep = () => setCurrentStep(prev => Math.max(0, prev - 1));
        const handleGoToStep = (event) => {
            const detail = typeof event.detail === 'object' ? event.detail : {step: event.detail};
            const step = detail.fieldPath ? stepForCvField(detail.fieldPath) : Number(detail.step);
            if (Number.isInteger(step) && step >= 0 && step < STEPS.length) {
                setCurrentStep(step);
                setCorrectionTarget(detail.fieldPath || detail.message || detail.title ? {...detail, step} : null);
            }
        };

        window.addEventListener('cvbuilder:prev-step', handlePrevStep);
        window.addEventListener('cvbuilder:go-to-step', handleGoToStep);
        return () => {
            window.removeEventListener('cvbuilder:prev-step', handlePrevStep);
            window.removeEventListener('cvbuilder:go-to-step', handleGoToStep);
        };
    }, []);

    useEffect(() => {
        if (!correctionTarget) return undefined;
        const timer = window.setTimeout(() => {
            const found = focusCvField(correctionTarget.fieldPath);
            if (!found) document.getElementById('cv-correction-target')?.scrollIntoView?.({block: 'center'});
        }, 80);
        return () => window.clearTimeout(timer);
    }, [correctionTarget, currentStep]);

    useEffect(() => {
        const handleAddExp = () => {
            addExperience();
            setCurrentStep(2); // Go to Experiences step
        };
        window.addEventListener('cvbuilder:add-experience', handleAddExp);
        return () => window.removeEventListener('cvbuilder:add-experience', handleAddExp);
    }, [addExperience]);

    const handleSave = async () => {
        setSaveError("");
        try {
            const newId = await saveDraft();
            if (!newId) {
                setSaveError("Impossible d'enregistrer le brouillon. Vérifiez votre connexion puis réessayez.");
                return false;
            }
            if (!routeDraftId) {
                pendingRouteSyncDraftIdRef.current = newId;
                navigate(`/admin/recrutement/cv-builder/${newId}`, { replace: true });
            }
            setLastSavedDraft(JSON.stringify(draft));
            setShowSaved(true);
            window.setTimeout(() => setShowSaved(false), 3000);
            return true;
        } catch {
            setSaveError("Impossible d'enregistrer le brouillon. Vérifiez votre connexion puis réessayez.");
            return false;
        }
    };

    const handleLibraryNavigation = (event) => {
        if (hasUnsavedChanges && !window.confirm("Des modifications ne sont pas encore enregistrées. Quitter quand même ?")) {
            event.preventDefault();
        }
    };

    const handleDuplicateAction = async (action) => {
        if (!action) {
            setDuplicatePrompt({ show: false, existingDrafts: [], payload: null });
            return;
        }

        setIsReplacing(true);
        try {
            const newId = await saveDraft(action);
            if (newId) {
                if (!routeDraftId) {
                    pendingRouteSyncDraftIdRef.current = newId;
                    navigate(`/admin/recrutement/cv-builder/${newId}`, { replace: true });
                }
                setLastSavedDraft(JSON.stringify(draft));
            }
        } catch {
            setSaveError("Impossible de remplacer le brouillon. Vérifiez votre connexion puis réessayez.");
        } finally {
            setIsReplacing(false);
            setDuplicatePrompt({ show: false, existingDrafts: [], payload: null });
        }
    };

    const handleGenerate = async () => {
        const generated = await generateDraft();
        if (!generated?.blob) {
            return;
        }
        downloadPdfBlob(generated.blob, generated.fileName);
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0: return <CandidateStep />;
            case 1: return <SkillsStep />;
            case 2: return <ExperiencesStep />;
            case 3: return <MissionsStep />;
            case 4: return <EducationStep />;
            case 5: return <PreviewStep />;
            default: return <CandidateStep />;
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="w-8 h-8 rounded-full border-2 border-lenvia-blue border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-20">
            <Dialog open={hasLocalDraft} onOpenChange={setHasLocalDraft}>
                <DialogContent className="sm:max-w-md bg-slate-900 border-white/10 text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="h-5 w-5 text-amber-500" />
                            Brouillon non sauvegardé détecté
                        </DialogTitle>
                        <DialogDescription className="text-slate-400 pt-2">
                            Souhaitez-vous reprendre votre dernière saisie locale ?
                            <br />
                            <span className="text-xs italic text-slate-500">
                                Si vous ignorez, le brouillon local sera définitivement supprimé.
                            </span>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex sm:justify-between gap-2 mt-4">
                        <button
                            onClick={handleIgnoreLocal}
                            className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition-colors border border-white/5 hover:bg-white/5 rounded-lg"
                        >
                            Ignorer
                        </button>
                        <button
                            onClick={handleResumeLocal}
                            className="px-6 py-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors shadow-lg shadow-amber-900/20"
                        >
                            Reprendre
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={duplicatePrompt.show} onOpenChange={(open) => {
                if (!open && !isReplacing) setDuplicatePrompt({ show: false, existingDrafts: [], payload: null });
            }}>
                <DialogContent className="sm:max-w-lg bg-slate-900 border-white/10 text-white" data-testid="cv-replacement-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base sm:text-lg font-bold text-white">
                            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                            Un CV existe déjà pour ce candidat
                        </DialogTitle>
                        <DialogDescription className="text-slate-300 pt-2 text-sm leading-relaxed space-y-2">
                            <span className="block">
                                Un CV LENVIA est déjà enregistré pour ce candidat.
                            </span>
                            <span className="block text-slate-400 text-xs">
                                Si vous continuez, le nouveau CV remplacera la version actuellement active. La version précédente ne sera plus le CV principal affiché dans la bibliothèque.
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    {/* Details Box */}
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-xs space-y-2 my-2">
                        <div className="flex justify-between items-center py-0.5">
                            <span className="text-slate-400">Candidat :</span>
                            <span className="font-semibold text-white">{duplicatePrompt.candidateName || draft.candidate?.full_name || "Sans nom"}</span>
                        </div>
                        {duplicatePrompt.existingCv?.title && (
                            <div className="flex justify-between items-center py-0.5">
                                <span className="text-slate-400">Fonction actuelle :</span>
                                <span className="text-slate-200">{duplicatePrompt.existingCv.title}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center py-0.5">
                            <span className="text-slate-400">Statut du CV actuel :</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/10 text-slate-200">
                                {duplicatePrompt.existingCv?.status === "generated" ? "Généré" : duplicatePrompt.existingCv?.status === "ready" ? "Prêt" : "Brouillon"}
                            </span>
                        </div>
                        {(duplicatePrompt.existingCv?.last_generated_at || duplicatePrompt.existingCv?.updated_at) && (
                            <div className="flex justify-between items-center py-0.5">
                                <span className="text-slate-400">Date version existante :</span>
                                <span className="text-slate-300 font-mono">
                                    {new Date(duplicatePrompt.existingCv.last_generated_at || duplicatePrompt.existingCv.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                </span>
                            </div>
                        )}
                        <div className="pt-2 border-t border-white/10 flex justify-between items-center">
                            <span className="text-slate-400">Nouveau CV à publier :</span>
                            <span className="font-medium text-emerald-400">{duplicatePrompt.newCv?.title || draft.candidate?.title || "Nouveau profil"}</span>
                        </div>
                    </div>

                    <p className="text-xs text-slate-300 font-medium">
                        Souhaitez-vous confirmer le remplacement ?
                    </p>

                    <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-3">
                        <button
                            type="button"
                            disabled={isReplacing}
                            onClick={() => handleDuplicateAction(null)}
                            className="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white transition-colors border border-white/10 hover:bg-white/5 rounded-lg disabled:opacity-50"
                            data-testid="cancel-cv-replacement-button"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            disabled={isReplacing}
                            onClick={() => handleDuplicateAction("replace")}
                            className="px-4 py-2 text-xs font-bold text-white bg-lenvia-blue hover:bg-blue-600 rounded-lg transition-colors shadow-md disabled:opacity-50 inline-flex items-center justify-center min-w-[170px]"
                            data-testid="confirm-cv-replacement-button"
                        >
                            {isReplacing ? (
                                <>
                                    <Loader2 className="animate-spin w-3.5 h-3.5 mr-2" />
                                    Remplacement en cours…
                                </>
                            ) : (
                                "Remplacer le CV existant"
                            )}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">

                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Link
                            to="/admin/recrutement/cv-library"
                            onClick={handleLibraryNavigation}
                            className="text-slate-400 hover:text-white flex items-center text-sm font-medium transition-colors"
                        >
                            &larr; Retour bibliothèque
                        </Link>
                    </div>
                    <h1 className="text-2xl font-heading font-bold text-white">CV Builder</h1>
                    <p className="mt-1 text-slate-400">
                        {routeDraftId ? `Édition du draft : ${draft.candidate?.full_name || routeDraftId}` : "Générez un CV LENVIA à partir de vos données"}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex flex-col items-end">
                        <span className="text-xs text-slate-300 font-semibold uppercase tracking-wide">Complétude</span>
                        <div className="w-32 h-2 bg-white/10 rounded-full mt-1.5 overflow-hidden" role="progressbar" aria-label="Complétude du CV" aria-valuemin={0} aria-valuemax={100} aria-valuenow={completionScore}>
                            <div
                                className={`h-full transition-[width] duration-500 ${completionScore >= 80 ? 'bg-emerald-500' : completionScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${completionScore}%` }}
                            ></div>
                        </div>
                        <span className="text-xs font-bold text-white mt-1">{completionScore}%</span>
                    </div>
                    <div className="flex min-w-[190px] items-center justify-end" aria-live="polite" aria-atomic="true">
                        {isSaving ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400">
                                <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
                                <span className="text-xs font-bold uppercase tracking-wide">Sauvegarde…</span>
                            </div>
                        ) : saveError ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-500/10 border border-rose-500/20 rounded-full text-rose-300" role="alert">
                                <AlertCircle aria-hidden="true" className="h-4 w-4" />
                                <span className="text-xs font-semibold">Échec de sauvegarde</span>
                            </div>
                        ) : showSaved ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 animate-in fade-in duration-300">
                                <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" />
                                <span className="text-xs font-bold uppercase tracking-wide">Sauvegardé</span>
                            </div>
                        ) : hasUnsavedChanges ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-500/10 border border-white/10 rounded-full text-slate-300">
                                <Clock3 aria-hidden="true" className="h-4 w-4" />
                                <span className="text-xs font-semibold">Modifications en attente</span>
                            </div>
                        ) : <span className="text-xs text-slate-400">Tout est enregistré</span>}
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800/80 border border-slate-600 rounded-lg hover:bg-slate-700 hover:text-white transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? "Sauvegarde..." : "Enregistrer"}
                    </button>
                    <button
                        onClick={handleGenerate}
                        disabled={isSaving}
                        title="Générer le PDF"
                        className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 transition-colors shadow-md disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed"
                    >
                        Générer PDF
                    </button>
                </div>
            </div>

            {saveError ? (
                <div className="flex flex-col gap-3 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-100 sm:flex-row sm:items-center sm:justify-between" role="alert">
                    <span>{saveError}</span>
                    <button type="button" onClick={handleSave} className="min-h-11 rounded-lg border border-rose-300/30 px-4 font-semibold hover:bg-rose-500/10">Réessayer</button>
                </div>
            ) : null}

            {mandatoryReview ? (
                <div className="flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-100" role="status">
                    <AlertCircle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                    <p>Certaines informations du CV peuvent nécessiter une relecture. Vous pouvez néanmoins générer votre document PDF ou Word immédiatement.</p>
                </div>
            ) : null}

            {importMode === "selection" && (
                <CvImportSelection 
                    onSelectManual={() => setImportMode("none")}
                    onSelectImport={() => setImportMode("upload")}
                />
            )}

            {(importMode === "upload" || importMode === "uploading") && (
                <CvImportStep 
                    onFileSelected={(file) => parseUploadedCv(file)}
                    isLoading={isUploading || importMode === "uploading"}
                    importProgress={importProgress}
                    importError={importError}
                    importWarnings={importWarnings}
                    manualReviewRequired={manualReviewRequired}
                    lastImportResult={lastImportResult}
                    onOpenBuilder={() => setImportMode("review")}
                    onRetry={() => {
                        resetImportState();
                        setImportMode("upload");
                    }}
                />
            )}

            {importMode === "scoring" && (
                <ConfidenceScoreView 
                    score={parseScore}
                    warnings={parseWarnings}
                    structuredWarnings={lastImportResult?.structured_warnings || lastImportResult?.warning_details}
                    sectionCoverage={lastImportResult?.source_section_coverage}
                    anomalies={anomalies}
                    scoringDimensions={scoringDimensions}
                    mandatoryReview={mandatoryReview}
                    validationStatus={validationStatus || lastImportResult?.validation_status}
                    verificationProgress={verificationProgress}
                    documentQuality={draft?.source_metadata?.document_quality || lastImportResult?.document_quality}
                    providerPrimary={lastImportResult?.provider_primary}
                    providerUsed={lastImportResult?.provider_used}
                    providerFallbackUsed={lastImportResult?.provider_fallback_used}
                    modelUsed={lastImportResult?.model_used}
                    onReview={() => setImportMode("review")}
                    onResolveAnomaly={resolveAnomaly}
                    onNavigateToStep={(target) => {
                        setImportMode("review");
                        window.dispatchEvent(new CustomEvent('cvbuilder:go-to-step', {detail: typeof target === 'object' ? target : {step: target}}));
                    }}
                />
            )}

            {correctionTarget && <div id="cv-correction-target" role="status" className="rounded-lg border border-amber-400/40 bg-amber-400/10 p-4 text-slate-100">
                <p className="font-semibold">{correctionTarget.title || 'Information à vérifier'} — {STEPS[correctionTarget.step]?.title}</p>
                <p className="mt-1 text-sm">{correctionTarget.message || 'Vérifiez les informations de cette rubrique avec le CV source. Aucune modification automatique n’a été appliquée.'}</p>
                {correctionTarget.sourceText && <blockquote className="mt-2 border-l-2 border-amber-400 pl-3 text-sm whitespace-pre-wrap">Extrait à comparer : {correctionTarget.sourceText}</blockquote>}
                <button type="button" className="mt-2 text-sm underline" onClick={() => setCorrectionTarget(null)}>Fermer cette indication</button>
            </div>}
            {(importMode === "none" || importMode === "review") && (
                <StepWizard
                    steps={STEPS}
                    currentStep={currentStep}
                    onStepChange={(step) => { setCorrectionTarget(null); setCurrentStep(step); }}
                    onFinish={handleGenerate}
                >
                    {renderStep()}
                </StepWizard>
            )}
        </div>
    );
};

const CvBuilderPage = () => {
    return (
        <CvBuilderProvider>
            <CvBuilderContent />
        </CvBuilderProvider>
    );
};

export default CvBuilderPage;
